import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Auth0Provider } from '@brighte/passwordless-authenticator';
import { getConfig } from '@config';
import { datadogRum } from '@datadog/browser-rum';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ENV_VARIABLES, getTrackingId, isDDRumKnownError } from '@utils';
import { AppWithLD } from './App';
import { AnalyticsProvider } from './analytics/provider';
import { queryClient } from './graphql/clients/reactQuery';
import { AppProvider } from './provider';

type Props = {
  rootId: string;
};

const createApp = ({ rootId }: Props) => {
  const container = document.getElementById(rootId);

  if (!container) {
    throw new Error('Oops, no container found.');
  }
  const config = datadogRum.getInitConfiguration();

  if (!ENV_VARIABLES.IS_DEV && !config) {
    const trackingId = getTrackingId();
    datadogRum.init({
      applicationId: ENV_VARIABLES.DATADOG_RUM_APP_ID,
      clientToken: ENV_VARIABLES.DATADOG_RUM_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'customer-application-form',
      env: ENV_VARIABLES.ENV ?? 'development',
      version: ENV_VARIABLES.GIT_COMMIT ?? '1.0.0',
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      beforeSend: isDDRumKnownError,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      startSessionReplayRecordingManually: true,
      proxy: getConfig().datadogProxy,
      allowedTracingUrls: [getConfig().url],
    });
    datadogRum.setGlobalContextProperty('trackingId', trackingId);
  }

  const root = createRoot(container);
  root.render(
    <StrictMode>
      <Auth0Provider
        userScope={getConfig().userScope}
        domain={ENV_VARIABLES.AUTH_DOMAIN}
        clientId={ENV_VARIABLES.AUTH_CLIENT_ID}
        datadogInstance={datadogRum}
      >
        <AppProvider>
          <QueryClientProvider client={queryClient()}>
            <AnalyticsProvider>
              {import.meta.env.DEV && <ReactQueryDevtools />}
              <AppWithLD />
            </AnalyticsProvider>
          </QueryClientProvider>
        </AppProvider>
      </Auth0Provider>
    </StrictMode>,
  );
};

export { createApp };
